import { v5 as uuidv5 } from "@-/com.npmjs.uuid";
import { normalizeToneNumbersPinyin } from "@-/dev.improve.haniverse.database.schemas._lib.types.pinyin";

/**
 * Row ids that two devices would otherwise both invent (see `database.md`, "Clean baseline and
 * identity").
 *
 * A row the app finds by a natural key (a character's meaning card, the note on a morpheme, the
 * Favorites collection) gets an id derived from that key. Two devices that create it before they
 * sync then create the SAME row, so its upload is an ordinary create-or-revive and nothing has to
 * be merged or rejected. This is what lets the actor database have no unique index besides its
 * primary keys; a unique index can reject a synced row forever.
 *
 * Rows that are events (a review, a trip, a study session) and rows the user creates on purpose (a
 * named collection) keep random ids.
 *
 * Every name below is stored data: changing the namespace, a kind string or the order of a key's
 * parts re-keys those rows on every device. Never change one.
 */
const ACTOR_ROW_ID_NAMESPACE = "f60161e4-ebb1-4e95-ab99-5f3b40eaa6c0";

function deriveActorRowId(...name: readonly string[]): string {
  // JSON keeps the parts apart: ("ab", "c") and ("a", "bc") must not derive the same id.
  return uuidv5(JSON.stringify(name), ACTOR_ROW_ID_NAMESPACE);
}

/** The one `user` row of an actor database. Literal so it is greppable; a test re-derives it. */
export const ACTOR_USER_ID = "4c41c678-da3f-5263-9ebb-d90cf03c5d44";

/** The Favorites `collection` row. Literal so it is greppable; a test re-derives it. */
export const FAVORITES_COLLECTION_ID = "e6b66964-dcdd-541a-89dc-0ba7b3a7d6a8";

export function deriveActorUserId(): string {
  return deriveActorRowId("user");
}

export function deriveFavoritesCollectionId(): string {
  return deriveActorRowId("collection", "favorites");
}

/** The three rows that make up one card. */
export interface ActorCardRowIds {
  /**
   * The overlay row that names the card's subject: `user_grapheme_meaning_card`,
   * `user_grapheme_pronunciation_card`, `user_word_sentence_card` or `user_quiz_card`.
   */
  detailId: string;
  /**
   * The card's schedule. A card has one `fsrs_card` row for good: removing the card from study
   * tombstones it, and enrolling the card again revives the same row with a fresh schedule.
   */
  fsrsCardId: string;
  userCardId: string;
}

function deriveCardRowIds(...subject: readonly string[]): ActorCardRowIds {
  return {
    detailId: deriveActorRowId("card-detail", ...subject),
    fsrsCardId: deriveActorRowId("fsrs_card", ...subject),
    userCardId: deriveActorRowId("user_card", ...subject),
  };
}

/** A meaning card is identified by its grapheme. */
export function deriveGraphemeMeaningCardRowIds(grapheme: string): ActorCardRowIds {
  return deriveCardRowIds("grapheme-meaning", grapheme);
}

/** A pronunciation card is identified by `(grapheme, pinyin)`. */
export function deriveGraphemePronunciationCardRowIds({
  grapheme,
  pinyin,
}: {
  grapheme: string;
  pinyin: string;
}): ActorCardRowIds {
  // The column stores the normalized reading, so the id is derived from the same form.
  return deriveCardRowIds("grapheme-pronunciation", grapheme, normalizeToneNumbersPinyin(pinyin));
}

/** A word card is identified by `(word, pinyin, kind)`. */
export function deriveWordSentenceCardRowIds({
  kind,
  pinyin,
  word,
}: {
  kind: string;
  pinyin: string;
  word: string;
}): ActorCardRowIds {
  return deriveCardRowIds("word-sentence", word, normalizeToneNumbersPinyin(pinyin), kind);
}

/** A quiz card is identified by its quiz's catalogue UUID `key`. */
export function deriveQuizCardRowIds(key: string): ActorCardRowIds {
  return deriveCardRowIds("quiz", key);
}

/** One membership row per `(collection, morpheme)`: removing tombstones it, adding revives it. */
export function deriveCollectionMorphemeId({
  collectionId,
  morpheme,
}: {
  collectionId: string;
  morpheme: string;
}): string {
  return deriveActorRowId("collection_morpheme", collectionId, morpheme);
}

export function deriveMorphemeNoteId(morpheme: string): string {
  return deriveActorRowId("morpheme_note", morpheme);
}

export function deriveMorphemeSearchVisitId(front: string): string {
  return deriveActorRowId("morpheme_search_visit", front);
}

/** `key` is the slide's catalogue UUID. */
export function deriveUserSlideId(key: string): string {
  return deriveActorRowId("user_slide", key);
}

export function deriveUserSentenceId(sentence: string): string {
  return deriveActorRowId("user_sentence", sentence);
}

export function deriveUserWordId(word: string): string {
  return deriveActorRowId("user_word", word);
}
